/**
 * Minimal reCAPTCHA v3 loader with explicit rendering, for forms that must keep working next to
 * `react-google-recaptcha-v3` providers on the same page.
 *
 * Why not the library: it shares one global script, and every provider's unmount cleanup removes
 * that script and sets `window.___grecaptcha_cfg = undefined`, which breaks every other widget on
 * the page (e.g. the footer's contact modal closing). A provider mounting while the script exists
 * also never renders its widget. This module checks the state on every use and reloads Google's
 * script when it was wiped. Browser-only: call it from effects and event handlers, never during
 * SSR.
 */

const API_URL = 'https://www.google.com/recaptcha/api.js';
const READY_TIMEOUT_MS = 10000;

export const RECAPTCHA_SCRIPT_ID = 'vansite-recaptcha-v3';

const ignore = () => {};

// Concurrent callers share one load; cleared once it settles so the next call re-checks state.
let pendingLoad = null;

// Google's bundle is loaded and its config intact.
const isUsable = () =>
  typeof window.grecaptcha?.render === 'function' && !!window.___grecaptcha_cfg;

// The library's provider cleanup leaves `window.grecaptcha` behind but drops its config.
const isWiped = () => !!window.grecaptcha && !window.___grecaptcha_cfg;

const ownScript = () => document.getElementById(RECAPTCHA_SCRIPT_ID);

// An api.js another form (the library) injected and that may still be loading.
const foreignScript = () =>
  document.querySelector(`script[src^="${API_URL}"]:not(#${RECAPTCHA_SCRIPT_ID})`);

// Without the stale object Google's loader creates a fresh `ready` queue the new bundle drains.
const discardStale = () => {
  ownScript()?.remove();
  delete window.grecaptcha;
};

const injectScript = (locale) => {
  ownScript()?.remove();
  const script = document.createElement('script');
  script.id = RECAPTCHA_SCRIPT_ID;
  script.src = `${API_URL}?render=explicit${locale ? `&hl=${encodeURIComponent(locale)}` : ''}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
};

const scriptLoaded = (script) =>
  new Promise((resolve, reject) => {
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', () => reject(new Error('reCAPTCHA script failed to load')), {
      once: true,
    });
  });

const grecaptchaReady = () =>
  new Promise((resolve) => window.grecaptcha.ready(() => resolve(window.grecaptcha)));

const withTimeout = (promise) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('reCAPTCHA did not become ready')), READY_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// `force` skips waiting for a foreign script and replaces anything not usable with our own.
const acquire = (locale, force) => {
  if (isUsable()) return Promise.resolve(window.grecaptcha);
  if (force || isWiped()) discardStale();
  // Google's loader ran (it defines the `ready` queue) and its bundle is still on the way.
  if (window.grecaptcha) return grecaptchaReady();
  const script = (!force && foreignScript()) || injectScript(locale);
  return scriptLoaded(script).then(grecaptchaReady);
};

/**
 * Resolves with a usable `window.grecaptcha`. Reuses one that is loaded (also when another form
 * loaded it), waits for a load in progress, and (re)injects the explicit-render script when there
 * is none or its config was wiped. Rejects when the script fails or is not ready in time.
 * @param {object} [options]
 * @param {string} [options.locale] - widget language (`hl`), only used when injecting
 * @param {boolean} [options.force] - inject our own script instead of waiting for a foreign one
 * @returns {Promise<object>}
 */
export const loadRecaptcha = ({ locale, force = false } = {}) => {
  if (!pendingLoad) {
    pendingLoad = withTimeout(acquire(locale, force)).then(
      (grecaptcha) => {
        pendingLoad = null;
        return grecaptcha;
      },
      (error) => {
        pendingLoad = null;
        throw error;
      }
    );
  }
  return pendingLoad;
};

/**
 * Renders an invisible v3 widget with an inline badge into a fresh slot inside `container`
 * (anything rendered there before is removed, so re-rendering never hits "already rendered").
 * @param {HTMLElement} container
 * @param {string} siteKey
 * @returns {number} widget id
 */
export const renderInvisibleWidget = (container, siteKey) => {
  while (container.firstChild) {
    container.firstChild.remove();
  }
  const slot = document.createElement('div');
  container.appendChild(slot);
  return window.grecaptcha.render(slot, { sitekey: siteKey, size: 'invisible', badge: 'inline' });
};

/**
 * @param {number} widgetId
 * @param {string} action
 * @returns {Promise<string|null>} the token, or null on any failure
 */
export const executeRecaptcha = async (widgetId, action) => {
  try {
    return (await window.grecaptcha.execute(widgetId, { action })) || null;
  } catch {
    return null;
  }
};

/**
 * One form's widget. `preload` loads and renders it ahead of time; `getToken` returns a fresh
 * token per call. If the first try fails (script wiped or missing, execute throws), it reloads
 * when needed, re-renders and tries once more before giving up with null.
 * @param {object} params
 * @param {string} params.siteKey
 * @param {string} [params.locale]
 * @param {() => HTMLElement|null} params.getContainer - element the badge renders into
 * @returns {{ preload: () => Promise<void>, getToken: (action: string) => Promise<string|null> }}
 */
export const createRecaptchaClient = ({ siteKey, locale, getContainer }) => {
  let widgetId = null;
  let preparing = null;

  const prepare = async (force) => {
    const rerender = force || widgetId === null || !isUsable();
    await loadRecaptcha({ locale, force });
    if (rerender) {
      widgetId = renderInvisibleWidget(getContainer(), siteKey);
    }
    return widgetId;
  };

  // A submit during the preload joins it instead of rendering a second widget.
  const prepareShared = (force) => {
    if (force || !preparing) {
      const current = prepare(force).finally(() => {
        if (preparing === current) preparing = null;
      });
      preparing = current;
    }
    return preparing;
  };

  const attempt = (action, force) =>
    prepareShared(force)
      .then((id) => executeRecaptcha(id, action))
      .catch(() => null);

  return {
    preload: () => prepareShared(false).then(ignore, ignore),
    getToken: async (action) => (await attempt(action, false)) || attempt(action, true),
  };
};
